import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectService } from '@/features/project';
import { TableUploadService } from '@/features/table-upload';
import { Project, ProjectUpdateRequest, SessionMember } from '@/shared/types/session';
import { FileInfo } from '@/shared/types/table-upload';
import { DEFAULT_AGENT_ID } from '@/config';

// Query Keys - centralized for consistency
export const projectKeys = {
    all: ['projects'] as const,
    lists: () => [...projectKeys.all, 'list'] as const,
    list: () => [...projectKeys.lists()] as const,
    details: () => [...projectKeys.all, 'detail'] as const,
    detail: (id: string) => [...projectKeys.details(), id] as const,
    files: (id: string) => [...projectKeys.detail(id), 'files'] as const,
    members: (id: string) => [...projectKeys.detail(id), 'members'] as const,
};

/**
 * Hook to fetch all projects for the current user
 * Used by: Sidebar, Dashboard (ProjectList)
 */
export function useProjectsQuery() {
    return useQuery({
        queryKey: projectKeys.list(),
        queryFn: async () => {
            const response = await ProjectService.getUserProjects();
            return response.projects;
        },
        staleTime: 0,
        refetchOnMount: 'always',
    });
}

/**
 * Hook to fetch a single project's details
 * Used by: ProjectView
 */
export function useProjectQuery(projectId: string | undefined) {
    return useQuery({
        queryKey: projectKeys.detail(projectId!),
        queryFn: () => ProjectService.getProject(projectId!),
        enabled: !!projectId,
        staleTime: 30_000,
    });
}

/**
 * Hook to fetch project files/uploads
 * Used by: ProjectView, FileList
 */
export function useProjectFilesQuery(projectId: string | undefined) {
    return useQuery({
        queryKey: projectKeys.files(projectId!),
        queryFn: async () => {
            const uploads = await TableUploadService.getProjectTableUploads(projectId!);
            return uploads.map(upload => TableUploadService.convertToFileInfo(upload));
        },
        enabled: !!projectId,
        staleTime: 30_000,
    });
}

/**
 * Hook to fetch project members
 * Used by: ProjectView, ProjectMembers
 */
export function useProjectMembersQuery(projectId: string | undefined) {
    return useQuery({
        queryKey: projectKeys.members(projectId!),
        queryFn: () => ProjectService.getProjectMembers(projectId!),
        enabled: !!projectId,
        staleTime: 60_000, // Members change less frequently
    });
}

/**
 * Hook to create a new project
 */
export function useCreateProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: { name: string; color?: string | null; icon?: string | null; tags?: string[] }) =>
            ProjectService.createProject({ name: data.name, agent_id: DEFAULT_AGENT_ID, color: data.color, icon: data.icon, tags: data.tags }),
        onSuccess: () => {
            // Invalidate projects list to trigger refetch
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

/**
 * Hook to delete a project
 */
export function useDeleteProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (projectId: string) => ProjectService.deleteProject(projectId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

/**
 * Hook to update a project
 */
export function useUpdateProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, data }: { projectId: string; data: ProjectUpdateRequest }) =>
            ProjectService.updateProject(projectId, data),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

/**
 * Hook to add a member to a project
 */
export function useAddProjectMember() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, email }: { projectId: string; email: string }) =>
            ProjectService.addProjectMember(projectId, { email }),
        onSuccess: (response, { projectId }) => {
            // Update cache optimistically with the returned member
            if (response.success && response.member) {
                queryClient.setQueryData<SessionMember[] | undefined>(projectKeys.members(projectId), (old) => {
                    if (!old) return [response.member];
                    return [...old, response.member];
                });
            }
        },
    });
}

/**
 * Hook to remove a member from a project
 */
export function useRemoveProjectMember() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, memberId }: { projectId: string; memberId: number }) =>
            ProjectService.removeProjectMember(projectId, memberId),
        onSuccess: (_, { projectId, memberId }) => {
            // Update cache optimistically
            queryClient.setQueryData<SessionMember[] | undefined>(projectKeys.members(projectId), (old) => {
                if (!old) return old;
                return old.filter((m) => m.id !== memberId);
            });
        },
    });
}

/**
 * Hook to update a member's role
 */
export function useUpdateMemberRole() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, memberId, role }: { projectId: string; memberId: number; role: 'owner' | 'member' }) =>
            ProjectService.updateMemberRole(projectId, memberId, role),
        onSuccess: (_, { projectId, memberId, role }) => {
            // Update cache optimistically
            queryClient.setQueryData<SessionMember[] | undefined>(projectKeys.members(projectId), (old) => {
                if (!old) return old;
                return old.map((m) => m.id === memberId ? { ...m, role } : m);
            });
        },
    });
}

/**
 * Hook to leave a project
 */
export function useLeaveProject() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (projectId: string) => ProjectService.leaveProject(projectId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}
